import {
  AplicarAcaoLotePayload,
  AplicarAcaoLoteResult,
  AplicarAcaoPayload,
  AplicarAcaoResult,
  CobrancaPreviaResult,
  DecisaoView,
  FilaIgnorada,
  FilaReforcoSugestao,
  FilaResult,
  MudancaDoDia,
  PreviaCobrancaPayload,
} from '@/domain/fila/interfaces/fila';
import { filaRepository } from '@/domain/fila/repositories/filaRepository';
import { logger } from '@/infrastructure/persistence/managers/LoggerManager';
import { createStore } from '@/lib/zustand';

interface FilaState {
  fila: FilaResult | null;
  historico: Array<DecisaoView>;
  /**
   * O dia que o historico carregado representa, ou `null` enquanto ninguem escolheu
   * um (a API responde o dia de hoje). A gaveta le daqui o valor do campo de data.
   */
  historicoDia: string | null;
  /** O que mudou hoje, do mais recente para o mais antigo; `null` enquanto nao carregou. */
  mudancasHoje: Array<MudancaDoDia> | null;
  mudancasLoading: boolean;
  mudancasFailed: boolean;
  loading: boolean;
  failed: boolean;
  /** A leitura do dia ainda esta em voo: o contador do botao nao pode dizer zero. */
  historicoLoading: boolean;
  historicoFailed: boolean;
  applyingUid: string | null;
  /** O lote em voo: a tela bloqueia a selecao inteira enquanto ele grava. */
  applyingLote: boolean;
  /** A decisao que esta sendo desfeita, para o botao da linha mostrar o progresso. */
  desfazendoDecisao: string | null;
  /** As OPs ignoradas; `null` enquanto a ficha nunca foi aberta. */
  ignoradas: Array<FilaIgnorada> | null;
  ignoradasLoading: boolean;
  ignoradasFailed: boolean;
  loadFila: () => Promise<void>;
  loadIgnoradas: () => Promise<void>;
  /** Relê a ficha depois de uma acao, e so se ela ja estiver na tela. */
  recarregarIgnoradas: () => Promise<void>;
  /** Le a sugestao de reforco. Throws para a tela mostrar a mensagem da API. */
  sugestaoDeReforco: (uid: string) => Promise<FilaReforcoSugestao>;
  /** Sem `dia` recarrega o dia que ja esta na tela; com `dia` troca para ele. */
  loadHistorico: (dia?: string) => Promise<void>;
  loadMudancasHoje: () => Promise<void>;
  aplicar: (uid: string, payload: AplicarAcaoPayload) => Promise<AplicarAcaoResult>;
  aplicarEmLote: (payload: AplicarAcaoLotePayload) => Promise<AplicarAcaoLoteResult>;
  /** Desfaz uma decisao. Throws para a tela mostrar a mensagem da API. */
  desfazerDecisao: (id: string) => Promise<AplicarAcaoResult>;
  /** Monta a previa da cobranca. Throws on failure so the caller shows the API message. */
  previaCobranca: (payload: PreviaCobrancaPayload) => Promise<CobrancaPreviaResult>;
}

const HISTORICO_PAGE_SIZE = 50;

// The header trigger and the queue page share this store and both load on mount: one
// request in flight serves every caller instead of hitting the (heavy) endpoint twice.
let filaInFlight: Promise<void> | null = null;
// O sino do cabecalho e o bloco da Inicio pedem as mudancas do dia juntos: uma requisicao so.
let mudancasInFlight: Promise<void> | null = null;
// A ficha "Ignoradas" recarrega junto da fila depois de cada acao: uma requisicao so.
let ignoradasInFlight: Promise<void> | null = null;
// Trocar o dia na gaveta algumas vezes seguidas: so o ULTIMO pedido pode escrever, senao
// a resposta lenta de um dia anterior sobrescreve a lista do dia que esta na tela.
let historicoSeq = 0;

export const filaStore = createStore<FilaState>((set, get) => ({
  fila: null,
  historico: [],
  historicoDia: null,
  mudancasHoje: null,
  mudancasLoading: false,
  mudancasFailed: false,
  loading: false,
  failed: false,
  historicoLoading: false,
  historicoFailed: false,
  applyingUid: null,
  applyingLote: false,
  desfazendoDecisao: null,
  ignoradas: null,
  ignoradasLoading: false,
  ignoradasFailed: false,

  loadFila: async () => {
    if (filaInFlight !== null) {
      return filaInFlight;
    }
    const run = async (): Promise<void> => {
      set({ loading: true });
      try {
        const fila = await filaRepository.getFila();
        set({ fila, failed: false });
      } catch (error) {
        logger.error('[filaStore] failed to load fila', error);
        set({ failed: true });
      } finally {
        set({ loading: false });
      }
    };
    filaInFlight = run();
    try {
      await filaInFlight;
    } finally {
      filaInFlight = null;
    }
  },

  loadIgnoradas: async () => {
    if (ignoradasInFlight !== null) {
      return ignoradasInFlight;
    }
    const run = async (): Promise<void> => {
      set({ ignoradasLoading: true });
      try {
        const ignoradas = await filaRepository.ignoradas();
        set({ ignoradas: ignoradas ?? [], ignoradasFailed: false });
      } catch (error) {
        logger.error('[filaStore] failed to load the ignoradas', error);
        set({ ignoradasFailed: true });
      } finally {
        set({ ignoradasLoading: false });
      }
    };
    ignoradasInFlight = run();
    try {
      await ignoradasInFlight;
    } finally {
      ignoradasInFlight = null;
    }
  },

  // Leitura pura: a sugestao nao move ninguem, entao nada no estado muda com ela.
  sugestaoDeReforco: async (uid) => {
    return filaRepository.sugestaoDeReforco(uid);
  },

  loadMudancasHoje: async () => {
    if (mudancasInFlight !== null) {
      return mudancasInFlight;
    }
    const run = async (): Promise<void> => {
      set({ mudancasLoading: true });
      try {
        const mudancas = await filaRepository.mudancasHoje();
        set({ mudancasHoje: mudancas ?? [], mudancasFailed: false });
      } catch (error) {
        logger.error('[filaStore] failed to load mudancas de hoje', error);
        set({ mudancasFailed: true });
      } finally {
        set({ mudancasLoading: false });
      }
    };
    mudancasInFlight = run();
    try {
      await mudancasInFlight;
    } finally {
      mudancasInFlight = null;
    }
  },

  loadHistorico: async (dia) => {
    const diaPedido = dia ?? get().historicoDia;
    historicoSeq += 1;
    const pedido = historicoSeq;
    set({ historicoDia: diaPedido, historicoLoading: true });
    try {
      const result = await filaRepository.historico({
        page: 1,
        limit: HISTORICO_PAGE_SIZE,
        data: diaPedido ?? undefined,
      });
      if (pedido !== historicoSeq) {
        return;
      }
      set({ historico: result.data ?? [], historicoFailed: false });
    } catch (error) {
      logger.error('[filaStore] failed to load historico', error);
      if (pedido === historicoSeq) {
        set({ historicoFailed: true });
      }
    } finally {
      if (pedido === historicoSeq) {
        set({ historicoLoading: false });
      }
    }
  },

  aplicar: async (uid, payload) => {
    set({ applyingUid: uid });
    try {
      const result = await filaRepository.aplicarAcao(uid, payload);
      set({ fila: result.fila, failed: false });
      await Promise.all([get().loadHistorico(), get().recarregarIgnoradas()]);
      return result;
    } finally {
      set({ applyingUid: null });
    }
  },

  aplicarEmLote: async (payload) => {
    set({ applyingLote: true });
    try {
      const result = await filaRepository.aplicarAcaoEmLote(payload);
      set({ fila: result.fila, failed: false });
      await Promise.all([get().loadHistorico(), get().recarregarIgnoradas()]);
      return result;
    } finally {
      set({ applyingLote: false });
    }
  },

  /**
   * Desfaz uma decisao. Recarrega o MESMO que aplicar recarrega, porque desfazer muda
   * exatamente as mesmas coisas: a fila (o item volta a nao resolvido), o historico do
   * dia (a linha passa a desfeita) e a ficha "Ignoradas".
   */
  desfazerDecisao: async (id) => {
    set({ desfazendoDecisao: id });
    try {
      const result = await filaRepository.desfazerDecisao(id);
      set({ fila: result.fila, failed: false });
      await Promise.all([get().loadHistorico(), get().recarregarIgnoradas()]);
      return result;
    } finally {
      set({ desfazendoDecisao: null });
    }
  },

  /**
   * Relê a ficha SO quando ela ja esta carregada: ignorar e voltar mudam a lista, e
   * quem nunca a abriu nao paga uma requisicao por acao aplicada.
   */
  recarregarIgnoradas: async () => {
    if (get().ignoradas === null) {
      return;
    }
    await get().loadIgnoradas();
  },

  // A previa nao altera a fila: ela nao mexe em `fila` nem recarrega o historico.
  previaCobranca: async (payload) => {
    return filaRepository.previaCobranca(payload);
  },
}));
